import { type ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BILLING_INTERVAL, type BillingInterval } from '@scio3/shared-types';
import { Loader2, Minus, Plus, ShoppingCart } from 'lucide-react';
import { Button, Input, Separator, RadioGroup, RadioGroupItem, Label, cn } from '@scio3/ui';
import type { UpcomingSubscriptionReviewData } from '@scio3/graphql-client';
import { StripeCardForm } from '../../components/StripeCardForm';
import { getPlanDisplayName, formatCurrencyAmount } from '../../utils/plan-helpers';
import { BILLING_INTERVAL_LABELS, isAccountFlatAddon } from '../../utils/billing-constants';

type EditableBundle = {
  _id?: string;
  maxQuantity: number;
  maxQuantityOption: number;
  expiredDate?: string;
};

/** Controlled numeric input that allows free typing, validates on blur */
function ScreenSpinInput({
  value,
  min,
  max,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  const [localValue, setLocalValue] = useState(String(value));
  useEffect(() => {
    setLocalValue(String(value));
  }, [value]);

  // Raw <input> is intentional here — this is a composite spin control fused between
  // two buttons. shadcn <Input> adds its own border/ring/padding that breaks the flush layout.
  return (
    <input
      type="text"
      inputMode="numeric"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value.replace(/\D/g, ''))}
      onBlur={() => {
        const num = parseInt(localValue, 10);
        if (isNaN(num) || num < min) {
          onChange(min);
          setLocalValue(String(min));
        } else if (num > max) {
          onChange(max);
          setLocalValue(String(max));
        } else {
          onChange(num);
          setLocalValue(String(num));
        }
      }}
      data-testid="confirmation-screen-count-input"
      className="w-12 text-center text-sm font-medium border-y border-border h-8 bg-primary text-white outline-none"
      aria-label="Number of screens"
    />
  );
}

type Props = {
  isTrial: boolean;
  /** #1411 — true when the account is currently on the Free plan (no active paid
   *  subscription). Like trial users, free-plan upgraders have no existing Stripe
   *  subscription to prorate against, so the backend review returns total = 0 even
   *  though the card is charged the full amount. Treated like `isTrial` for the
   *  Account Summary display so "Due Today" reflects the real charge (= Subtotal). */
  isFreePlan?: boolean;
  /** #1413 — true when the account's subscription is cancelled/inactive and the user
   *  is re-subscribing through this wizard. Same proration gap as trial/free: with no
   *  active Stripe subscription the backend review returns total = 0 even though the
   *  card is charged the full amount. Treated like `isTrial` so "Due Today" = Subtotal. */
  isAccountInactive?: boolean;
  existingCard: { brand: string; last4: string } | null;
  useExistingCard: boolean;
  onUseExistingCardChange: (value: boolean) => void;
  stripeLoading: boolean;
  stripeClientSecret: string | null;
  stripePaymentMethodOrder: string[];
  /** Return URL for Stripe redirect (encodes charge params for iDEAL/SEPA/Bancontact) */
  stripeReturnUrl?: string;
  defaultEmail: string;
  /** Awaitable so the wrapper below can run a `finally` to reset
   *  stripeProcessing after handleCharge fully resolves (covers the
   *  no-changes early-return path that doesn't fire the isSubmitting useEffect). */
  onStripeSuccess: (id: string) => Promise<void> | void;
  isSubmitting: boolean;
  billingInterval: BillingInterval;
  currentPricePerScreen: number;
  screenCount: number;
  onScreenCountChange: (value: number) => void;
  minScreens: number;
  /** Original screen count before changes (limitation.maxQuantity) */
  originalScreenCount: number;
  /** Disable screen count editing (e.g. in upgrade mode where only plan changes) */
  disableScreenCount?: boolean;
  /** Addon total price matching scio1's getSubtotalPrice addon calculation */
  addonTotal: number;
  /** Individual addon line items for Summary display */
  addonItems: Array<{
    key: string;
    name: string;
    pricePerUnit: number;
    quantity: number;
    total: number;
    /** Tier within a multi-tier addon (e.g. 'standard' | 'pro').
     *  When set, name renders with "({{Tier}})" suffix — scio1 parity. */
    planFeature?: string;
    /** Catalogue flags behind `isAccountFlatAddon` — a flat licence covers the account, so its
     *  row carries no screen count. */
    type?: string;
    enableDeviceManage?: boolean;
  }>;
  currency: string;
  selectedPlan: string;
  /** Real display name of the plan being charged (from the resolved plan-feature doc),
   *  e.g. "Pro" for a legacy pro account — `selectedPlan` is display-normalized to proPlus
   *  for card highlighting and would mislabel the summary as "Pro Plus". */
  planSummaryName?: string;
  /** Plan interval name from plans[currency][interval].name, e.g. "Yearly" */
  planEditName?: string;
  /** Plan discount percentage from plans[currency][interval].discount, e.g. 10 */
  planEditDiscount?: number;
  reviewResult: UpcomingSubscriptionReviewData | null;
  reviewLoading: boolean;
  /** Credit from getUpcomingSubscription (matching scio1's creditPrice from initFinishPayment) */
  initialCredit: number;
  onConfirmExistingCard: () => void;
  promoCode: string | null;
  onPromoCodeChange: (value: string | null) => void;
  showPromoCode: boolean;
  onShowPromoCode: () => void;
  /** Called when billing address changes in AddressElement — for redirect flow address capture */
  onBillingAddressChange?: (address: Record<string, string> | null) => void;
  /** Called when Stripe form starts processing — for parent loading state */
  onStripeSubmitStart?: () => void;
  /** Called when Stripe client-side validation/confirmSetup fails — parent
   *  must reset its own stripeProcessing flag, otherwise the wizard footer
   *  button stays stuck on "Processing..." (#1117). */
  onStripeSubmitError?: () => void;
  /**
   * Team-billing mode (Issue #52). When true, the payment-method affordances
   * are read-only: no card-selection RadioGroup, no inline "new card" Stripe
   * form, and no "(change)" link on the summary payment-method line. Team
   * billing admins change cards via the Payment Settings tab — scio1 parity.
   */
  teamMode?: boolean;
  /** Per-bundle free-device state (SCIO 1.0 subscriptionPlanModal.js `deviceFrees`).
   *  Only used when `billingInterval === yearly_freedevice`. Empty otherwise. */
  editedDeviceFrees?: EditableBundle[];
  /** Seed `editedDeviceFrees` from `existingDeviceFrees` + the current screenCount
   *  on enter-to-Confirmation. Fire once per step entry. */
  onInitDeviceFrees?: () => void;
  /** Update a single bundle's quantity (min 0, max maxQuantityOption). */
  onBundleQuantityChange?: (index: number, qty: number) => void;
};

export function WizardStepConfirmation({
  isTrial,
  isFreePlan = false,
  isAccountInactive = false,
  existingCard,
  useExistingCard,
  onUseExistingCardChange,
  stripeLoading,
  stripeClientSecret,
  stripePaymentMethodOrder,
  stripeReturnUrl,
  defaultEmail,
  onStripeSuccess,
  isSubmitting,
  billingInterval,
  currentPricePerScreen,
  screenCount,
  onScreenCountChange,
  minScreens,
  originalScreenCount,
  disableScreenCount,
  addonTotal,
  addonItems,
  currency,
  selectedPlan,
  planSummaryName,
  planEditName,
  planEditDiscount,
  reviewResult,
  reviewLoading,
  initialCredit,
  onConfirmExistingCard,
  promoCode,
  onPromoCodeChange,
  showPromoCode,
  onShowPromoCode,
  onBillingAddressChange,
  onStripeSubmitStart,
  onStripeSubmitError,
  teamMode,
  editedDeviceFrees,
  onInitDeviceFrees,
  onBundleQuantityChange,
}: Props) {
  const { t } = useTranslation();

  // Seed per-bundle state once when we enter Confirmation on yearly_freedevice.
  const didInitBundlesRef = useRef(false);
  useEffect(() => {
    if (billingInterval === BILLING_INTERVAL.YEARLY_FREEDEVICE && !didInitBundlesRef.current) {
      onInitDeviceFrees?.();
      didInitBundlesRef.current = true;
    }
    if (billingInterval !== BILLING_INTERVAL.YEARLY_FREEDEVICE) {
      didInitBundlesRef.current = false;
    }
  }, [billingInterval, onInitDeviceFrees]);

  // Per-bundle mode kicks in only when yearly_freedevice AND we have bundles to edit.
  const isFreeDeviceBundleMode =
    billingInterval === BILLING_INTERVAL.YEARLY_FREEDEVICE && (editedDeviceFrees?.length ?? 0) > 0;
  const [stripeProcessing, setStripeProcessing] = useState(false);
  const handleSubmitStart = useCallback(() => {
    setStripeProcessing(true);
    onStripeSubmitStart?.();
  }, [onStripeSubmitStart]);

  // Reset stripeProcessing when charge completes (success or failure).
  // stripeProcessing is set true on form submit (onSubmitStart) but only reset
  // by onError for Stripe errors. When Stripe succeeds but charge fails,
  // stripeProcessing stays true → button stuck on "Processing..." spinner.
  const prevIsSubmitting = useRef(isSubmitting);
  useEffect(() => {
    if (prevIsSubmitting.current && !isSubmitting && stripeProcessing) {
      setStripeProcessing(false);
    }
    prevIsSubmitting.current = isSubmitting;
  }, [isSubmitting, stripeProcessing]);

  // Reset stripeProcessing after the parent's onSuccess (handleStripeSuccess
  // → handleCharge) resolves. Covers the no-changes early-return path where
  // handleCharge toasts and returns without ever toggling isSubmitting in a
  // way React renders (both setIsSubmitting calls are batched into a single
  // no-op render → useEffect above never sees the transition). Relies on
  // StripeCardForm's `await onSuccess(...)` (added in 740e3e078) so this
  // finally runs only after handleCharge fully completes.
  // (claude-dev/bugfix-anhluuquoc/2026-05-05-batch-04/01-plan.md Option B)
  const wrappedStripeSuccess = useCallback(
    async (id: string) => {
      try {
        await onStripeSuccess(id);
      } finally {
        setStripeProcessing(false);
      }
    },
    [onStripeSuccess],
  );

  // Matching scio1 isShowConfirmForExpired():
  // Trial without card -> simple "Payment method" (just Stripe form)
  // Paid or has card -> full "Confirmation" with order items + summary + payment
  const isShowConfirm = !isTrial || !!existingCard;
  // Matching scio1's getSubtotalPrice(true): normalScreens * price + addonTotal
  const clientSubtotal = screenCount * currentPricePerScreen + addonTotal;
  // Plan edit text matching scio1: "Yearly + 10%" from planEdit.name + planEdit.discount.
  // For yearly_freedevice always show "Yearly + Free Device" (SCIO 1.0 parity) —
  // BE sometimes returns just "Yearly" as the plan name for the FD tier, which
  // would mis-label the card/summary.
  const planEditText =
    billingInterval === BILLING_INTERVAL.YEARLY_FREEDEVICE
      ? t('Yearly + Free Device')
      : planEditName
        ? `${planEditName}${planEditDiscount ? ` + ${planEditDiscount}%` : ''}`
        : BILLING_INTERVAL_LABELS[billingInterval];
  // Plan display for summary — just the plan name (e.g. "Standard"). The
  // billing interval is already shown in the "Billing Cycle" row above
  // (#1175 — scio1 parity, subscriptionPlanModal.html shows "Standard"
  // with "N screens" below; including the interval here was duplicating it).
  // Prefer the resolved plan's real name (e.g. "Pro" for a legacy pro account); fall back to
  // the display-normalized selectedPlan only until the plan-feature doc loads.
  const planSummaryText = planSummaryName ?? getPlanDisplayName(selectedPlan);

  // Promo code UI -- matching scio1: "Enter Promo code" link -> label + input (no Apply button)
  // scio1 just stores the value; promo is validated on "Update subscription" click
  // Rendered inline next to email via emailInlineSlot
  const promoCodeUI: ReactNode = isTrial ? (
    showPromoCode ? (
      <div className="w-[200px] space-y-2">
        <Label className="text-sm text-muted-foreground">{t('Promo code')}</Label>
        <Input
          type="text"
          className="w-full uppercase h-[44px]"
          value={promoCode ?? ''}
          onChange={(e) => onPromoCodeChange(e.target.value.toUpperCase() || null)}
        />
      </div>
    ) : (
      <Button
        type="button"
        variant="link"
        size="sm"
        className="text-sm text-primary underline cursor-pointer whitespace-nowrap mb-2 h-auto p-0"
        onClick={onShowPromoCode}
      >
        {t('Enter Promo code')}
      </Button>
    )
  ) : null;

  // Stripe payment form UI (reused)
  const stripeFormUI = (
    <div className="space-y-3">
      {existingCard && !teamMode && (
        <RadioGroup
          value={useExistingCard ? 'existing' : 'new'}
          onValueChange={(v) => onUseExistingCardChange(v === 'existing')}
          className="space-y-2"
        >
          <Label
            htmlFor="payment-existing"
            className={cn(
              'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
              useExistingCard ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent/50',
            )}
          >
            <RadioGroupItem
              value="existing"
              id="payment-existing"
              className="shrink-0"
              data-testid="wizard-payment-existing-radio"
            />
            <span className="text-sm">
              {existingCard.last4
                ? `${existingCard.brand} ****${existingCard.last4}`
                : existingCard.brand}
            </span>
          </Label>
          <Label
            htmlFor="payment-new"
            className={cn(
              'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
              !useExistingCard ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent/50',
            )}
          >
            <RadioGroupItem
              value="new"
              id="payment-new"
              className="shrink-0"
              data-testid="wizard-payment-new-radio"
            />
            <span className="text-sm">{t('New payment method')}</span>
          </Label>
        </RadioGroup>
      )}
      {!useExistingCard && (
        <div className="mt-3">
          {stripeLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : stripeClientSecret ? (
            <StripeCardForm
              clientSecret={stripeClientSecret}
              paymentMethodOrder={stripePaymentMethodOrder}
              returnUrl={stripeReturnUrl}
              defaultEmail={defaultEmail}
              onSuccess={wrappedStripeSuccess}
              onError={(msg) => {
                toast.error(msg);
                setStripeProcessing(false);
                onStripeSubmitError?.();
              }}
              onSubmitStart={handleSubmitStart}
              submitText={t('Update subscription')}
              showSubmitButton={false}
              isLoading={isSubmitting}
              showBillingAddress
              onBillingAddressChange={onBillingAddressChange}
            />
          ) : (
            <div className="rounded-lg border border-border p-4 text-center text-sm text-muted-foreground">
              {t('Loading payment form...')}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // === TRIAL layout: simple "Payment method" -- just Stripe form ===
  // Matching scio1: Email (with "Enter Promo code" inline) -> Card/US bank tabs -> form fields
  if (!isShowConfirm) {
    return (
      <div className="py-4">
        <h1 className="text-xl font-semibold text-center mb-6">{t('Payment method')}</h1>
        <div className="rounded-lg border p-6">
          {stripeLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : stripeClientSecret ? (
            <StripeCardForm
              clientSecret={stripeClientSecret}
              paymentMethodOrder={stripePaymentMethodOrder}
              returnUrl={stripeReturnUrl}
              defaultEmail={defaultEmail}
              emailInlineSlot={promoCodeUI}
              onSuccess={wrappedStripeSuccess}
              onError={(msg) => {
                toast.error(msg);
                setStripeProcessing(false);
                onStripeSubmitError?.();
              }}
              onSubmitStart={handleSubmitStart}
              submitText={t('Update subscription')}
              showSubmitButton={false}
              isLoading={isSubmitting}
              showBillingAddress
              onBillingAddressChange={onBillingAddressChange}
            />
          ) : (
            <div className="text-center text-sm text-muted-foreground py-8">
              {t('Loading payment form...')}
            </div>
          )}
        </div>
      </div>
    );
  }

  // === PAID layout: 2-column "Confirmation" matching scio1 (Image #36) ===
  // Left: order items (plan + editable screen count + total)
  // Right: Summary sidebar (billing cycle, plan, subtotal, credit, total, payment method, Update button)
  // Screen change warning text — matching scio1's getTextBucketNormal()
  const bucketText = (() => {
    if (isTrial) return '';
    if (screenCount > originalScreenCount) {
      const added = screenCount - originalScreenCount;
      return `${added} ${t('screens added is allocated to this bucket')}`;
    }
    if (screenCount < originalScreenCount) {
      const reduce = originalScreenCount - screenCount;
      return t('subscriptionPlanModal5', {
        reduce: String(reduce),
        maxQuantityNormalOrigin: String(originalScreenCount),
        getMaxQuantityNormal: String(screenCount),
      });
    }
    return '';
  })();

  // Subtotal = always client-calculated (screens × price), matching scio1's getSubtotalPrice()
  const displaySubtotal = clientSubtotal;
  // #1175 — scio1 parity (subscriptionPlanModal.js:1742-1759):
  //   getCreditPrice() { return isTrial ? 0.00 : creditPrice; }
  //   getTotalPrice()  { return isTrial ? getSubtotalPrice() : totalPrice; }
  // Trial users haven't created a Stripe subscription yet so there's no
  // proration to compute — total = subtotal, credit = 0.
  // #1411 — Free-plan upgraders are the same case: a Free account has no active
  // paid Stripe subscription, so backend getUpcomingSubscriptionReview returns
  // total = 0 (nothing to prorate from) even though the card IS charged the full
  // amount. Without this, "Due Today" wrongly showed $0.00 while the customer was
  // billed the real Subtotal. Treat free like trial: total = subtotal, credit = 0.
  // #1413 — Cancelled/inactive accounts re-subscribing are the SAME case: the prior
  // Stripe subscription is gone, so the review returns total = 0 while the card is
  // charged in full. Without this they saw "Due Today = $0.00" too.
  // Paid users: trust whatever backend's getUpcomingSubscriptionReview
  // returns. Backend already factors in available credit, partial-period
  // proration, and any pending invoice items. Falling back to clientSubtotal
  // would WRONGLY ignore the user's existing credit (e.g. a $40 subtotal
  // covered by $30 of credit should display $10 — backend returns 10, fallback
  // would show 40). The reviewLoading spinner covers the "API not yet
  // returned" gap.
  const hasNoActiveSubscription = isTrial || isFreePlan || isAccountInactive;
  const displayCredit = hasNoActiveSubscription ? 0 : initialCredit;
  const backendTotal = reviewResult?.total ?? 0;
  const displayTotal: number = hasNoActiveSubscription ? clientSubtotal : backendTotal;

  return (
    <div className="py-4">
      <h1 className="text-xl font-semibold text-center mb-6">{t('Confirmation')}</h1>

      <div className="grid gap-4 grid-cols-1 lg:grid-cols-[1fr_320px]">
        {/* Left -- Order items + Payment method.
            #1194 — In upgrade-mode (`disableScreenCount=true`) we used to hide
            the entire left column, which also unmounted the Stripe payment
            form, so the "(change)" link did nothing and the "Update
            Subscription" submit button (form="stripe-payment-form") had no
            form to submit. We now keep the column rendered and only suppress
            the screen-count spinner + per-plan Total inside the plan card. */}
        <div className="space-y-4">
          {isFreeDeviceBundleMode ? (
            <DeviceFreeBundleCards
              bundles={editedDeviceFrees ?? []}
              pricePerScreen={currentPricePerScreen}
              currency={currency}
              planLabel={planEditText}
              onQuantityChange={(idx, qty) => onBundleQuantityChange?.(idx, qty)}
              t={t}
            />
          ) : (
            <div className="rounded-lg border bg-card p-6">
              <div className="flex items-center gap-6">
                {/* Cart icon + plan info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-semibold">{planEditText}</p>
                  <p className="text-sm text-primary">
                    {formatCurrencyAmount(currentPricePerScreen, currency)} {t('per screen')}
                  </p>
                  {/* Screen change warning — matching scio1 text-warning */}
                  {bucketText && <p className="text-sm text-amber-600 mt-2">{bucketText}</p>}
                </div>

                {/* Editable screen count + per-plan Total — both hidden in
                    upgrade mode (plan change only). #1194 keeps the plan card
                    structure rendered so the payment card below stays mounted. */}
                {!disableScreenCount && (
                  <>
                    <div className="text-center">
                      <p className="text-sm font-semibold mb-2">{t('Screens')}</p>
                      <div className="flex items-center gap-0">
                        <Button
                          data-testid="confirmation-screen-count-decrement-btn"
                          variant="default"
                          size="icon-sm"
                          onClick={() => {
                            if (screenCount > minScreens) onScreenCountChange(screenCount - 1);
                          }}
                          disabled={screenCount <= minScreens}
                          aria-label={t('Decrease')}
                          className={cn(
                            'rounded-r-none shrink-0',
                            screenCount <= minScreens
                              ? 'opacity-50 cursor-not-allowed'
                              : 'cursor-pointer',
                          )}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <ScreenSpinInput
                          value={screenCount}
                          min={minScreens}
                          max={9999}
                          onChange={onScreenCountChange}
                        />
                        <Button
                          data-testid="confirmation-screen-count-increment-btn"
                          variant="default"
                          size="icon-sm"
                          onClick={() => onScreenCountChange(screenCount + 1)}
                          aria-label={t('Increase')}
                          className="rounded-l-none shrink-0 cursor-pointer"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Total — plan only, matching scio1 (addons shown separately in Summary) */}
                    <div className="text-right min-w-[100px]">
                      <p className="text-lg font-semibold">
                        {formatCurrencyAmount(screenCount * currentPricePerScreen, currency)}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Payment method card — matching scio1 showPaymentMethod():
              shown when no card OR when user clicked "(change)".
              Team-billing (#52): always hidden — payment method is read-only
              in the team update-subscription flow, managed via Payment
              Settings tab instead. */}
          {(!existingCard || !useExistingCard) && !teamMode && (
            <div className="rounded-lg border bg-card p-6 space-y-3">
              <h4 className="text-sm font-semibold">{t('Payment method')}</h4>
              {stripeFormUI}
            </div>
          )}
        </div>

        {/* Right -- Summary sidebar */}
        <div className="rounded-lg border bg-card p-6 space-y-3 self-start">
          <h4 className="text-base font-semibold">{t('Account Summary')}</h4>

          <div className="flex justify-between text-sm">
            <span className="font-semibold">{t('Billing Cycle')}</span>
            <span>{planEditText}</span>
          </div>

          <div>
            <div className="flex justify-between text-sm">
              <span className="font-semibold">{planSummaryText}</span>
              <span>{formatCurrencyAmount(currentPricePerScreen, currency)}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {screenCount} {t('screens')}
            </p>
          </div>

          {/* Addon line items — matching scio1's separate addon rows.
              Tiered addons render as "{name} Add-on — {Tier}" (em-dash, singular).
              Non-tiered addons render plain "{name}" (existing behavior). */}
          {addonItems.map((addon) => {
            const tierLabel =
              addon.planFeature === 'pro'
                ? t('Pro')
                : addon.planFeature === 'standard'
                  ? t('Standard')
                  : null;
            const displayName = tierLabel
              ? `${addon.name} ${t('Add-on')} — ${tierLabel}`
              : addon.name;
            return (
              <div key={addon.key}>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">{displayName}</span>
                  <span>{formatCurrencyAmount(addon.pricePerUnit, currency)}</span>
                </div>
                {/* An account-flat licence is bought once for the whole account, so a screen
                    count under its name states something untrue about what is being charged.
                    Same predicate as the add-on cards and the receipt — see isAccountFlatAddon. */}
                {!isAccountFlatAddon(addon) && (
                  <p className="text-xs text-muted-foreground">
                    {addon.quantity} {addon.quantity === 1 ? t('screen') : t('screens')}
                  </p>
                )}
              </div>
            );
          })}

          <div className="flex justify-between text-sm">
            <span className="font-semibold">{t('Subtotal')}</span>
            <span data-testid="confirmation-subtotal">
              {formatCurrencyAmount(displaySubtotal, currency)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('Credit for previous purchase')}</span>
            <span data-testid="confirmation-credit" className="text-primary">
              {reviewLoading ? (
                <Loader2 className="h-3 w-3 animate-spin inline" />
              ) : (
                `${displayCredit < 0 ? '-' : ''}${formatCurrencyAmount(Math.abs(displayCredit), currency)}`
              )}
            </span>
          </div>
          {/* Sales Tax row intentionally hidden in Summary (#1178). Backend
              still returns reviewResult.taxs for parity / future surfacing,
              but the displayed Total already includes tax (Stripe
              invoice.amount_due is tax-inclusive), so showing a separate
              line here was confusing — Subtotal+Tax did not visually
              reconcile to Total because Total also reflects proration. */}
          <Separator />

          <div className="flex justify-between gap-3">
            <div>
              <p data-testid="confirmation-due-today-label" className="text-base font-semibold">
                {t('Due Today')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('Prorated charge for plan change')}
              </p>
            </div>
            <span
              data-testid="confirmation-total"
              className="text-base font-semibold whitespace-nowrap"
            >
              {reviewLoading ? (
                <Loader2 className="h-3 w-3 animate-spin inline" />
              ) : (
                formatCurrencyAmount(displayTotal, currency)
              )}
            </span>
          </div>

          {/* Payment method info with (change) link.
              Team-billing (#52): read-only — omit the "(change)" affordance,
              admins change cards via Payment Settings tab per scio1 parity. */}
          {existingCard && useExistingCard && (
            <div className="text-center text-xs text-muted-foreground">
              {t('Payment method')} <span className="uppercase">{existingCard.brand}</span>
              {existingCard.last4 ? ` - ${existingCard.last4}` : ''}
              {!teamMode && (
                <>
                  {' ('}
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="text-primary cursor-pointer h-auto p-0"
                    onClick={() => onUseExistingCardChange(false)}
                    data-testid="wizard-payment-change-btn"
                  >
                    {t('change')}
                  </Button>
                  {')'}
                </>
              )}
            </div>
          )}

          {/* Update subscription button inside sidebar.
              #1310 / team-billing — when `teamMode=true` the Stripe form is
              hidden (line 546), so the new-card path's form="stripe-payment-form"
              submit would silently no-op. Always route team-mode clicks through
              onConfirmExistingCard (charge against the team's card on file;
              backend rejects with a clear error if missing). Non-team flow
              keeps the dual button (existing-card vs new-card Stripe submit). */}
          <Button
            className="w-full cursor-pointer mt-2"
            onClick={useExistingCard || teamMode ? onConfirmExistingCard : undefined}
            type={!useExistingCard && !teamMode ? 'submit' : 'button'}
            form={!useExistingCard && !teamMode ? 'stripe-payment-form' : undefined}
            disabled={
              isSubmitting ||
              stripeProcessing ||
              reviewLoading ||
              (!useExistingCard && !teamMode && !stripeClientSecret)
            }
            data-testid="wizard-sidebar-submit-btn"
          >
            {isSubmitting || stripeProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t('Processing...')}
              </>
            ) : (
              t('Update subscription')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Per-bundle free-device cards — SCIO 1.0 parity (subscriptionPlanModal.html:365-417).
 * One card per entry in `bundles`, with its own +/- spinner capped by
 * `maxQuantityOption` (existing bundles = original qty, new bundles = 9999).
 * Retention warning per bundle from `getTextBucketFreeDevice`.
 */
function DeviceFreeBundleCards({
  bundles,
  pricePerScreen,
  currency,
  planLabel,
  onQuantityChange,
  t,
}: {
  bundles: EditableBundle[];
  pricePerScreen: number;
  currency: string;
  planLabel: string;
  onQuantityChange: (index: number, qty: number) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const formatDate = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };
  const retentionText = (b: EditableBundle): string => {
    // SCIO 1.0 getTextBucketFreeDevice: existing bundle with an expiredDate →
    // retention warning; new bundle (no _id) → "N screens added to this bucket".
    if (!b._id) {
      return `${b.maxQuantity} ${t('screens added is allocated to this bucket')}`;
    }
    if (!b.expiredDate) return '';
    const dateStr = formatDate(b.expiredDate);
    return t(
      'To keep Free Device, subscription required to stay until {{expiredDate}}, If required period has not passed, you still can cancel the subscription, but no refund will be credited',
      { expiredDate: dateStr },
    );
  };
  return (
    <>
      {bundles.map((bundle, i) => {
        if (bundle.maxQuantity <= 0 && !bundle.expiredDate) return null;
        const atMin = bundle.maxQuantity <= 0;
        const atMax = bundle.maxQuantity >= bundle.maxQuantityOption;
        return (
          <div key={bundle._id ?? `new-${i}`} className="rounded-lg border p-6">
            <div className="flex items-start gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-sm font-semibold">{planLabel}</p>
                <p className="text-sm text-primary">
                  {formatCurrencyAmount(pricePerScreen, currency)} {t('per screen')}
                </p>
                {retentionText(bundle) && (
                  <p className="text-sm text-amber-600 mt-2">{retentionText(bundle)}</p>
                )}
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold mb-2">{t('Screens')}</p>
                <div className="flex items-center gap-0">
                  <Button
                    variant="default"
                    size="icon-sm"
                    onClick={() => onQuantityChange(i, bundle.maxQuantity - 1)}
                    disabled={atMin}
                    aria-label={t('Decrease')}
                    className={cn(
                      'rounded-r-none shrink-0',
                      atMin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                    )}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <ScreenSpinInput
                    value={bundle.maxQuantity}
                    min={0}
                    max={bundle.maxQuantityOption}
                    onChange={(v) => onQuantityChange(i, v)}
                  />
                  <Button
                    variant="default"
                    size="icon-sm"
                    onClick={() => onQuantityChange(i, bundle.maxQuantity + 1)}
                    disabled={atMax}
                    aria-label={t('Increase')}
                    className={cn(
                      'rounded-l-none shrink-0',
                      atMax ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                    )}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="text-right min-w-[100px]">
                <p className="text-lg font-semibold">
                  {formatCurrencyAmount(pricePerScreen * bundle.maxQuantity, currency)}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
